<script setup lang="ts">
/**
 * 通用事件字段渲染器
 *
 * 根据 eventEditorSchema 定义的 control 类型，渲染 text/number/select 等控件并双向绑定事件属性。
 */
import { computed, watch } from 'vue'
import AppSelectField from '@/components/AppSelectField.vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import { GoodsStatus, GoodsTypeEnum } from '@/model/MallNewClass/Goods'
import type EventBase from '@/model/EventClass/EventBase'
import { useSceneEditorStore } from '@/store/sceneEditor'
import { useWorkspaceStore } from '@/store/workspace'
import type { EventFieldControl, EventFieldOption, EventFieldSchema } from './eventEditorSchema'
import { stripEmoji } from '@/utils/plainTextInput'

// props
const props = defineProps<{
  event: EventBase
  field: EventFieldSchema
}>()

// store
const editor = useSceneEditorStore()
const workspace = useWorkspaceStore()

// computed
const currentValue = computed({
  get: () => (props.event as unknown as Record<string, unknown>)[props.field.key],
  set: (value: unknown) => {
    ;(props.event as unknown as Record<string, unknown>)[props.field.key] = value
  },
})

const normalizedControl = computed<EventFieldControl>(() => props.field.control ?? 'text')

/** boolean 字段依赖修订号，避免仅改 Parameter 时复选框与列表文案不同步。 */
const booleanChecked = computed(() => {
  void editor.sceneEventLabelRevision
  return Boolean((props.event as unknown as Record<string, unknown>)[props.field.key])
})

const storyLockGoodsOptions = computed<EventFieldOption[]>(() => {
  const goodsList = workspace.project?.GameMallNew?.GoodsList ?? []
  return goodsList
    .filter((item) =>
      Number(item.GoodsType) === GoodsTypeEnum.Map
      && Number(item.Good_Status) === GoodsStatus.IsUp,
    )
    .map((item) => ({
      label: `${item.GoodsName || '未命名商品'}（ID: ${item.GoodsId}）`,
      value: item.GoodsId,
    }))
})
const storyLockSelectedValue = computed(() => {
  const currentId = Number(currentValue.value ?? 0)
  if (storyLockGoodsOptions.value.some((item) => Number(item.value) === currentId)) return currentId
  return Number(storyLockGoodsOptions.value[0]?.value ?? 0)
})

const isStack = computed(() => {
  return (
    normalizedControl.value === 'textarea' ||
    normalizedControl.value === 'resource-path' ||
    normalizedControl.value === 'string-list'
  )
})

// helpers
/** 将 select 的字符串值转为与当前字段类型一致的值。 */
function coerceSelectValue(rawValue: string, options: EventFieldOption[] | undefined) {
  const optionValue = options?.find((item) => String(item.value) === rawValue)?.value
  if (optionValue !== undefined) return optionValue
  const sample = currentValue.value
  if (typeof sample === 'number') return Number(rawValue)
  if (typeof sample === 'boolean') return rawValue === 'true'
  return rawValue
}

// handlers
function onTextInput(event: Event) {
  const el = event.target as HTMLInputElement
  const sanitized = stripEmoji(el.value)
  if (sanitized !== el.value) el.value = sanitized
  currentValue.value = sanitized
}

function onTextareaInput(event: Event) {
  const el = event.target as HTMLTextAreaElement
  const sanitized = stripEmoji(el.value)
  if (sanitized !== el.value) el.value = sanitized
  currentValue.value = sanitized
}

function onBooleanChange(event: Event) {
  const checked = (event.target as HTMLInputElement).checked
  ;(props.event as unknown as Record<string, unknown>)[props.field.key] = checked
  editor.notifySceneEventValueChanged()
}

function onSelectValue(value: string | number | boolean) {
  currentValue.value = coerceSelectValue(String(value), props.field.options)
}

function onStringListInput(event: Event) {
  const sanitized = stripEmoji((event.target as HTMLTextAreaElement).value)
  currentValue.value = sanitized
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean)
}

function onResourcePathChange(value: string) {
  currentValue.value = value
  editor.notifySceneEventValueChanged()
}

function writeStoryLockGoods(goodsId: number) {
  const eventRecord = props.event as unknown as Record<string, unknown>
  const goods = workspace.project?.GameMallNew?.GoodsList.find((item) => Number(item.GoodsId) === goodsId)
  currentValue.value = goodsId
  eventRecord.Description = goods ? `剧情锁：${goods.GoodsName || goods.GoodsId}` : ''
}

function onStoryLockGoodsValue(value: string | number | boolean) {
  writeStoryLockGoods(Number(value))
  editor.notifySceneEventValueChanged()
}

const storyLockSelectOptions = computed(() => (
  storyLockGoodsOptions.value.length
    ? storyLockGoodsOptions.value
    : [{ label: '没有剧情商品', value: 0 }]
))

// watch
watch(
  () => [normalizedControl.value, storyLockSelectedValue.value] as const,
  ([control, goodsId]) => {
    if (control !== 'story-lock-goods' || goodsId <= 0) return
    const currentId = Number(currentValue.value ?? 0)
    if (currentId === goodsId) return
    writeStoryLockGoods(goodsId)
    editor.notifySceneEventValueChanged()
  },
  { immediate: true },
)
</script>

<template>
  <div class="field-renderer" :class="{ 'field-renderer--stack': isStack }">
    <input
      v-if="normalizedControl === 'text'"
      class="cell-input wide"
      type="text"
      :value="String(currentValue ?? '')"
      :placeholder="field.placeholder"
      @input="onTextInput"
    />

    <textarea
      v-else-if="normalizedControl === 'textarea'"
      class="cell-input area"
      :rows="field.rows ?? 5"
      :placeholder="field.placeholder"
      :value="String(currentValue ?? '')"
      @input="onTextareaInput"
    />

    <NumberStepperField
      v-else-if="normalizedControl === 'number'"
      class="cell-input sm"
      :min="field.min"
      :max="field.max"
      :step="field.step ?? 1"
      :model-value="Number.isFinite(currentValue as number) ? Number(currentValue) : 0"
      @update:model-value="currentValue = $event"
    />

    <input
      v-else-if="normalizedControl === 'boolean'"
      type="checkbox"
      :checked="booleanChecked"
      @change="onBooleanChange"
    />

    <AppSelectField
      v-else-if="normalizedControl === 'select'"
      wide
      :model-value="(currentValue ?? '') as string | number"
      :options="field.options ?? []"
      @update:model-value="onSelectValue"
    />

    <AppSelectField
      v-else-if="normalizedControl === 'story-lock-goods'"
      wide
      :model-value="storyLockSelectedValue"
      :options="storyLockSelectOptions"
      @update:model-value="onStoryLockGoodsValue"
    />

    <ResourceSelectField
      v-else-if="normalizedControl === 'resource-path'"
      :model-value="String(currentValue ?? '')"
      :category="field.resourceCategory"
      display-mode="full"
      store-mode="engine"
      :placeholder="field.placeholder"
      @update:model-value="onResourcePathChange"
    />

    <textarea
      v-else-if="normalizedControl === 'string-list'"
      class="cell-input area"
      :rows="field.rows ?? 6"
      :placeholder="field.placeholder ?? '每行一项'"
      :value="Array.isArray(currentValue) ? currentValue.join('\n') : ''"
      @input="onStringListInput"
    />
  </div>
</template>

<style scoped>
.field-renderer {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--app-space-md);
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.field-renderer--stack {
  flex-wrap: nowrap;
  align-items: stretch;
}

.cell-input {
  color: var(--theme-text-heading);
  background: var(--theme-bg-surface);
  border: 1px solid var(--theme-bg-secondary);
  padding: var(--app-space-xs) var(--app-space-md);
  font-size: var(--app-font-size-md);
  box-sizing: border-box;
}

.cell-input.wide {
  width: 100%;
  min-width: 0;
}

.cell-input.sm {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 0;
}

.cell-input.area {
  width: 100%;
  min-height: 96px;
  resize: vertical;
  font-family: inherit;
}

.cell-input:focus {
  outline: none;
  border-color: var(--theme-accent);
}

:deep(.resource-select-field) {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
</style>
